import { useState } from "react";
import Marquee from "react-fast-marquee";
import { Medal, UsersThree, Stack, Truck, Check, Plus } from "@phosphor-icons/react";
import { IMG, COLLECTIONS, PRODUCTS, QUALITY, PARTNER, CLIENTS } from "../../data";
import { Reveal, Overline, MaskLineInView } from "./primitives";
import { useSelection } from "./SelectionContext";

const ICONS = { Medal, UsersThree, Stack, Truck };

export const Clients = () => (
  <section data-testid="clients-strip" className="relative z-10 py-10 border-b border-black/10 bg-[var(--bg-secondary)]">
    <p className="text-center font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-6">
      Trusted by hospitality teams across the UK &amp; beyond
    </p>
    <Marquee speed={34} gradient={false} autoFill>
      {CLIENTS.map((c) => (
        <span key={c} className="mx-8 font-serif italic text-2xl sm:text-3xl text-[var(--text-secondary)] flex items-center gap-8">
          {c} <span className="text-[var(--gold)] not-italic text-lg">✦</span>
        </span>
      ))}
    </Marquee>
  </section>
);

export const Collections = () => (
  <section id="collections" data-testid="collections-section" className="relative z-10 px-6 md:px-12 lg:px-16 py-24 md:py-32">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
      <div className="md:col-span-7">
        <Reveal><Overline>Our Collections</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            <MaskLineInView>A house of eight</MaskLineInView>
            <MaskLineInView delay={0.08} className="italic text-[var(--brass)]">signature ranges.</MaskLineInView>
          </h2>
        </Reveal>
      </div>
      <div className="md:col-span-4 md:col-start-9 md:pt-4">
        <Reveal delay={0.15}>
          <p className="text-[var(--text-secondary)] text-lg leading-relaxed">
            From tableware and barware to games, planters, scent and bespoke gifting — one supplier for the objects that define your rooms.
          </p>
        </Reveal>
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
      {COLLECTIONS.map((c, i) => (
        <Reveal key={c.name} delay={(i % 4) * 0.06}>
          <a href="#catalog" data-testid={`collection-${i}`} className="group block">
            <div className="overflow-hidden bg-[var(--bg-tertiary)]">
              <img src={c.img} alt={c.name} className="w-full aspect-[4/5] object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
            </div>
            <h3 className="mt-4 font-serif text-2xl text-[var(--text-primary)] group-hover:text-[var(--brass)] transition-colors duration-500">{c.name}</h3>
            <p className="mt-1 font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">{c.tag}</p>
          </a>
        </Reveal>
      ))}
    </div>
  </section>
);

export const Catalog = () => {
  const { has, toggle } = useSelection();
  const ranges = ["All", ...COLLECTIONS.map((c) => c.name)];
  const [active, setActive] = useState("All");
  const shown = active === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.range === active);

  return (
    <section id="catalog" data-testid="catalog-section" className="relative z-10 px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-black/10">
      <div className="mb-12">
        <Reveal><Overline>The Selection</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight">Build your selection</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 text-[var(--text-secondary)] max-w-xl leading-relaxed">
            Add the pieces you love — then send us your selection and we'll return a tailored quote, samples and customisation options for your volumes.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="flex flex-wrap gap-2 mb-12">
          {ranges.map((r) => (
            <button
              key={r}
              data-testid={`filter-${r}`}
              onClick={() => setActive(r)}
              className={`border px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase transition-colors duration-300 ${active === r ? "bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)]" : "border-black/20 text-[var(--text-secondary)] hover:border-black/50"}`}
            >
              {r}
            </button>
          ))}
        </div>
      </Reveal>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
        {shown.map((p, i) => {
          const selected = has(p.id);
          return (
            <Reveal key={p.id} delay={(i % 4) * 0.05}>
              <div data-testid={`product-${p.id}`} className="group">
                <div className="relative overflow-hidden bg-[var(--bg-tertiary)]">
                  <img src={p.img} alt={p.name} className="w-full aspect-square object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  <button
                    data-testid={`add-${p.id}`}
                    onClick={() => toggle(p)}
                    className={`absolute bottom-0 inset-x-0 h-11 flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase transition-colors duration-300 ${selected ? "bg-[var(--gold)] text-[#2B2620]" : "bg-[var(--text-primary)]/90 text-[var(--bg-primary)] hover:bg-[var(--text-primary)]"}`}
                  >
                    {selected ? <><Check size={14} weight="bold" /> Added</> : <><Plus size={14} weight="bold" /> Add to selection</>}
                  </button>
                </div>
                <h3 className="mt-4 font-serif text-xl text-[var(--text-primary)]">{p.name}</h3>
                <div className="mt-1 flex items-center justify-between">
                  <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">{p.range}</p>
                  <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--gold)]">{p.moq}</p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

export const Bespoke = () => (
  <section id="bespoke" data-testid="bespoke-section" className="relative z-10 px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-black/10 bg-[var(--bg-secondary)]">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
      <div className="md:col-span-6">
        <Reveal><Overline>Bespoke &amp; Custom</Overline></Reveal>
        <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          <MaskLineInView>Any object,</MaskLineInView>
          <MaskLineInView delay={0.08} className="italic text-[var(--brass)]">made to your theme.</MaskLineInView>
        </h2>
        <Reveal delay={0.2}>
          <p className="mt-8 text-[var(--text-secondary)] text-lg leading-relaxed max-w-lg">
            Every product is shaped around your theme, your cuisine and how you want the world to perceive you. Bespoke materials, colourways, engraving, embossing and packaging — matched to your brand and interior, and produced at the volumes you need.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--text-secondary)]">
            {["Theme & cuisine led", "Custom materials", "Brand engraving", "Bespoke packaging"].map((t) => (
              <span key={t} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]" />{t}</span>
            ))}
          </div>
        </Reveal>
      </div>
      <div className="md:col-span-5 md:col-start-8">
        <Reveal delay={0.15}>
          <div className="overflow-hidden group">
            <img src={IMG.bespoke} alt="Bespoke embossed detail" className="w-full aspect-[4/5] object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Quality = () => (
  <section id="quality" data-testid="quality-section" className="relative z-10 px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-black/10">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
      <div className="md:col-span-5">
        <Reveal><Overline>Quality &amp; Service</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Made for the<br /><span className="italic text-[var(--brass)]">demands of service.</span>
          </h2>
        </Reveal>
      </div>
      <div className="md:col-span-6 md:col-start-7 md:pt-4">
        <Reveal delay={0.15}>
          <p className="text-[var(--text-secondary)] text-lg leading-relaxed">
            We supply groups that cannot compromise on consistency. Every order is managed end-to-end — specified, sampled, quality-controlled and delivered — so your brand looks flawless in every location.
          </p>
        </Reveal>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
      {QUALITY.map((q, i) => {
        const Icon = ICONS[q.icon];
        return (
          <Reveal key={q.title} delay={i * 0.08}>
            <div data-testid={`quality-${i}`} className="border-t border-black/15 pt-6">
              <Icon size={30} className="text-[var(--gold)]" weight="thin" />
              <h3 className="mt-5 font-serif text-2xl text-[var(--text-primary)]">{q.title}</h3>
              <p className="mt-3 text-[var(--text-secondary)] leading-relaxed">{q.desc}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

export const Process = () => (
  <section id="process" data-testid="process-section" className="relative z-10 px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-black/10 bg-[var(--bg-secondary)]">
    <div className="mb-16">
      <Reveal><Overline>How We Partner</Overline></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight">Five simple stages</h2>
      </Reveal>
    </div>

    <div>
      {PARTNER.map((p, i) => (
        <Reveal key={p.n} delay={i * 0.05}>
          <div data-testid={`process-${p.n}`} className="grid grid-cols-12 gap-4 items-end py-8 border-t border-black/10 last:border-b">
            <span className="col-span-3 md:col-span-2 font-serif text-5xl sm:text-6xl outline-num leading-none">{p.n}</span>
            <h3 className="col-span-9 md:col-span-4 font-serif text-2xl sm:text-3xl text-[var(--text-primary)]">{p.title}</h3>
            <p className="col-span-12 md:col-span-6 mt-2 md:mt-0 text-[var(--text-secondary)] leading-relaxed">{p.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);
